import React from "react";
import { Button } from "primereact/button";

import { OnboardingState, StageCount } from "../../../../types/onboarding";

interface StageCountsProps {
  counts: StageCount[];
  selected: OnboardingState | null;
  onSelect: (state: OnboardingState | null) => void;
}

/**
 * How the prospect pipeline is distributed (CWA-94), computed from the list the
 * page already loaded. Selecting a stage drives the same filter as the dropdown.
 *
 * Stages with nobody in them are omitted — a row of zeroes is noise, not
 * information.
 *
 * Built on PrimeReact's Button rather than hand-rolled utility classes: the
 * first attempt styled the selected chip with `bg-primary text-white`, which
 * renders white-on-white in this theme and made the selected stage invisible.
 * Letting the theme own both states avoids guessing at its palette.
 *
 * The count sits in the label rather than in a Button `badge` for the same
 * reason — the badge rendered as a filled dot with the digit lost inside it.
 */
const StageCounts: React.FC<StageCountsProps> = ({
  counts,
  selected,
  onSelect,
}) => {
  if (!counts.length) return null;

  return (
    <div className="flex flex-wrap gap-2 mb-3" data-testid="stage-counts">
      {counts.map(({ state, label, count }) => {
        const isSelected = selected === state;
        return (
          <Button
            key={state}
            label={`${label} (${count})`}
            size="small"
            outlined={!isSelected}
            severity="secondary"
            aria-pressed={isSelected}
            // Clicking the selected stage again clears the filter, so the row
            // never becomes a trap the dropdown has to undo.
            onClick={() => onSelect(isSelected ? null : state)}
          />
        );
      })}
    </div>
  );
};

export default StageCounts;
